/**
 * Shared PermissionEditor — modular component for managing role assignments.
 *
 * Used by Doc, Folder, and Credential permission editors.
 * Supports both user and group assignments with configurable role hierarchies.
 */
import {
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  RowInput,
  Scrollable,
  useConfirmModal,
} from '@affine/component';
import { getObjectPermissionAreaBlockedNotice as getAreaBlockedNotice } from '@affine/core/modules/permissions/utils/object-permission-ui';
import { useI18n } from '@affine/i18n';
import {
  DeleteIcon,
  GroupIcon,
  PlusIcon,
  SearchIcon,
  UserIcon,
} from '@blocksuite/icons/rc';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  GroupInfo,
  MemberInfo,
  PermissionEditorAdapter,
  PermissionEditorChangeEvent,
  PermissionEditorConfig,
  PermissionEntry,
} from './permission-editor-types';
import { SettingsEntityAvatar } from './settings-entity';
import * as styles from './settings-permission.css';

type InviteMode = 'users' | 'groups';

interface PermissionEditorProps {
  config: PermissionEditorConfig;
  adapter: PermissionEditorAdapter;
  deferred?: boolean;
  emptyMessage?: ReactNode;
  onPermissionChanged?: (event: PermissionEditorChangeEvent) => void;
  presentation?: 'access' | 'full' | 'people';
}

export const PermissionEditor = ({
  config,
  adapter,
  deferred = false,
  emptyMessage,
  onPermissionChanged,
  presentation = 'full',
}: PermissionEditorProps) => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();

  const [entries, setEntries] = useState<PermissionEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Invite state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteMode, setInviteMode] = useState<InviteMode>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>(
    config.defaultGrantRole
  );

  // Find current owner
  const currentOwner = useMemo(() => {
    if (!config.ownerRole) return undefined;
    return entries.find(e => e.role === config.ownerRole);
  }, [entries, config.ownerRole]);

  // Separate user and group entries
  const userEntries = useMemo(
    () => entries.filter(e => e.type === 'user'),
    [entries]
  );
  const groupEntries = useMemo(
    () => entries.filter(e => e.type === 'group'),
    [entries]
  );

  // =========================================================================
  // Data Loading
  // =========================================================================

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await adapter.loadEntries();
      setEntries(result);
    } catch (err) {
      console.debug('Failed to load permission entries:', err);
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [adapter]);

  const loadMembers = useCallback(async () => {
    try {
      const result = await adapter.loadMembers(searchQuery);
      setMembers(result);
    } catch (err) {
      console.debug('Failed to load members:', err);
    }
  }, [adapter, searchQuery]);

  const loadGroups = useCallback(async () => {
    try {
      const result = await adapter.loadGroups();
      setGroups(result);
    } catch (err) {
      console.debug('Failed to load groups:', err);
    }
  }, [adapter]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    if (showInvite && inviteMode === 'users') {
      void loadMembers();
    }
    if (config.allowGroups !== false && showInvite && inviteMode === 'groups') {
      void loadGroups();
    }
  }, [showInvite, inviteMode, loadMembers, loadGroups, config.allowGroups]);

  // Filter out already-assigned members/groups
  const availableMembers = useMemo(() => {
    const assignedIds = new Set(userEntries.map(e => e.targetId));
    return members.filter(m => !assignedIds.has(m.id));
  }, [members, userEntries]);

  const availableGroups = useMemo(() => {
    const assignedIds = new Set(groupEntries.map(e => e.targetId));
    return groups.filter(g => !assignedIds.has(g.id));
  }, [groups, groupEntries]);

  const emitPermissionChanged = useCallback(
    (event: PermissionEditorChangeEvent) => {
      try {
        onPermissionChanged?.(event);
      } catch (err) {
        console.debug('Permission change side effect failed:', err);
      }
    },
    [onPermissionChanged]
  );

  // =========================================================================
  // Actions
  // =========================================================================

  const handleGrantUser = useCallback(
    async (userId: string) => {
      setIsUpdating(true);
      try {
        await adapter.grantUser(userId, selectedRole);
        const member = members.find(candidate => candidate.id === userId);
        emitPermissionChanged({
          action: 'grant',
          role: selectedRole,
          targetId: userId,
          targetEmail: member?.email,
          targetName: member?.name,
          type: 'user',
        });
        if (!deferred) {
          notify.success({
            title:
              t['com.streamlain.settings.workspace.permissions.granted']?.() ??
              'Permission granted',
          });
        }
        setShowInvite(false);
        setSearchQuery('');
        await loadEntries();
      } catch (err: unknown) {
        console.error('Failed to grant permission:', err);
        const gqlError =
          err instanceof Error
            ? (err as { graphQLErrors?: Array<{ message: string }> })
                .graphQLErrors?.[0]?.message
            : null;
        const baseTitle =
          t['com.streamlain.settings.workspace.permissions.grant-failed']?.() ??
          'Failed to grant permission';
        notify.error({
          title: gqlError ? `${baseTitle}: ${gqlError}` : baseTitle,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      adapter,
      deferred,
      selectedRole,
      emitPermissionChanged,
      loadEntries,
      members,
      t,
    ]
  );

  const handleGrantGroup = useCallback(
    async (groupId: string) => {
      setIsUpdating(true);
      try {
        await adapter.grantGroup(groupId, selectedRole);
        const group = groups.find(candidate => candidate.id === groupId);
        emitPermissionChanged({
          action: 'grant',
          role: selectedRole,
          targetId: groupId,
          targetName: group?.name,
          type: 'group',
        });
        if (!deferred) {
          notify.success({
            title:
              t['com.streamlain.settings.workspace.permissions.granted']?.() ??
              'Permission granted',
          });
        }
        setShowInvite(false);
        await loadEntries();
      } catch (err: unknown) {
        console.error('Failed to grant group permission:', err);
        const gqlError =
          err instanceof Error
            ? (err as { graphQLErrors?: Array<{ message: string }> })
                .graphQLErrors?.[0]?.message
            : null;
        const baseTitle =
          t['com.streamlain.settings.workspace.permissions.grant-failed']?.() ??
          'Failed to grant permission';
        notify.error({
          title: gqlError ? `${baseTitle}: ${gqlError}` : baseTitle,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      adapter,
      deferred,
      selectedRole,
      emitPermissionChanged,
      groups,
      loadEntries,
      t,
    ]
  );

  const handleTransferOwnership = useCallback(
    async (entry: PermissionEntry, newOwnerName: string) => {
      if (!config.ownerRole || !config.demoteOnTransferRole) return;

      const currentOwnerName =
        currentOwner?.type === 'user'
          ? currentOwner.name || currentOwner.email
          : (currentOwner?.name ?? 'the current owner');

      openConfirmModal({
        title:
          t[
            'com.streamlain.settings.workspace.permissions.transfer-ownership-title'
          ]?.() ?? 'Transfer Ownership',
        description:
          t[
            'com.streamlain.settings.workspace.permissions.transfer-ownership-description'
          ]?.({
            currentOwner: currentOwnerName,
            newOwner: newOwnerName,
          }) ??
          `This will transfer ownership from ${currentOwnerName} to ${newOwnerName}. ${currentOwnerName} will be demoted.`,
        confirmText:
          t[
            'com.streamlain.settings.workspace.permissions.transfer-ownership-confirm'
          ]?.() ?? 'Transfer',
        cancelText: t['Cancel']?.() ?? 'Cancel',
        confirmButtonOptions: { variant: 'primary' },
        onConfirm: async () => {
          setIsUpdating(true);
          try {
            // 1. Demote current owner
            if (currentOwner) {
              await adapter.updateRole(
                currentOwner,
                config.demoteOnTransferRole!
              );
            }
            // 2. Set new owner
            await adapter.updateRole(entry, config.ownerRole!);

            notify.success({
              title:
                t[
                  'com.streamlain.settings.workspace.permissions.ownership-transferred'
                ]?.() ?? 'Ownership transferred',
            });

            await loadEntries();
          } catch (err: unknown) {
            console.error('Failed to transfer ownership:', err);
            const gqlError =
              err instanceof Error
                ? (err as { graphQLErrors?: Array<{ message: string }> })
                    .graphQLErrors?.[0]?.message
                : null;
            const baseTitle =
              t[
                'com.streamlain.settings.workspace.permissions.transfer-failed'
              ]?.() ?? 'Failed to transfer ownership';
            notify.error({
              title: gqlError ? `${baseTitle}: ${gqlError}` : baseTitle,
            });
          } finally {
            setIsUpdating(false);
          }
        },
      });
    },
    [config, currentOwner, adapter, loadEntries, openConfirmModal, t]
  );

  const handleUpdateRole = useCallback(
    async (entry: PermissionEntry, newRole: string) => {
      // Special handling for Owner role
      if (config.ownerRole && newRole === config.ownerRole) {
        const name =
          entry.type === 'user' ? entry.name || entry.email : entry.name;
        void handleTransferOwnership(entry, name);
        return;
      }

      setIsUpdating(true);
      try {
        await adapter.updateRole(entry, newRole);
        emitPermissionChanged({
          action: 'update',
          role: newRole,
          targetId: entry.targetId,
          targetEmail: entry.type === 'user' ? entry.email : undefined,
          targetName: entry.name,
          type: entry.type,
        });
        if (!deferred) {
          notify.success({
            title:
              t['com.streamlain.settings.workspace.permissions.updated']?.() ??
              'Permission updated',
          });
        }
        await loadEntries();
      } catch (err: unknown) {
        console.error('Failed to update permission:', err);
        const gqlError =
          err instanceof Error
            ? (err as { graphQLErrors?: Array<{ message: string }> })
                .graphQLErrors?.[0]?.message
            : null;
        const baseTitle =
          t[
            'com.streamlain.settings.workspace.permissions.update-failed'
          ]?.() ?? 'Failed to update permission';
        notify.error({
          title: gqlError ? `${baseTitle}: ${gqlError}` : baseTitle,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      config,
      adapter,
      deferred,
      emitPermissionChanged,
      loadEntries,
      handleTransferOwnership,
      t,
    ]
  );

  const handleRevoke = useCallback(
    (entry: PermissionEntry) => {
      const name =
        entry.type === 'user' ? entry.name || entry.email : entry.name;

      openConfirmModal({
        title:
          t[
            'com.streamlain.settings.workspace.permissions.revoke-confirm-title'
          ]?.() ?? 'Remove access',
        description:
          t[
            'com.streamlain.settings.workspace.permissions.revoke-confirm-description'
          ]?.({ name }) ?? `Are you sure you want to remove ${name}'s access?`,
        confirmText: t['Remove']?.() ?? 'Remove',
        cancelText: t['Cancel']?.() ?? 'Cancel',
        confirmButtonOptions: { variant: 'error' },
        onConfirm: async () => {
          setIsUpdating(true);
          try {
            await adapter.revoke(entry);
            emitPermissionChanged({
              action: 'revoke',
              role: null,
              targetId: entry.targetId,
              targetEmail: entry.type === 'user' ? entry.email : undefined,
              targetName: entry.name,
              type: entry.type,
            });
            if (!deferred) {
              notify.success({
                title:
                  t[
                    'com.streamlain.settings.workspace.permissions.revoked'
                  ]?.() ?? 'Access removed',
              });
            }
            await loadEntries();
          } catch (err: unknown) {
            console.error('Failed to revoke permission:', err);
            const gqlError =
              err instanceof Error
                ? (err as { graphQLErrors?: Array<{ message: string }> })
                    .graphQLErrors?.[0]?.message
                : null;
            const baseTitle =
              t[
                'com.streamlain.settings.workspace.permissions.revoke-failed'
              ]?.() ?? 'Failed to remove access';
            notify.error({
              title: gqlError ? `${baseTitle}: ${gqlError}` : baseTitle,
            });
          } finally {
            setIsUpdating(false);
          }
        },
      });
    },
    [adapter, deferred, emitPermissionChanged, loadEntries, openConfirmModal, t]
  );

  // =========================================================================
  // Helpers
  // =========================================================================

  const getRoleLabel = useCallback(
    (role: string) => {
      const roleLabelKey = config.roleLabelKeys?.[role];
      if (roleLabelKey) {
        return t[roleLabelKey as keyof typeof t]?.() ?? role;
      }

      return (
        t[
          `com.streamlain.settings.workspace.permissions.role.${role.toLowerCase()}`
        ]?.() ?? role
      );
    },
    [config.roleLabelKeys, t]
  );

  // =========================================================================
  // Render
  // =========================================================================

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Loading size={16} />
        <span>
          {t[
            'com.streamlain.settings.workspace.permissions.loading-permissions'
          ]?.() ?? 'Loading permissions...'}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.editorContainer}>
      {/* Header with Add button */}
      <div className={styles.editorHeader}>
        <span className={styles.editorLabel}>
          {t[
            'com.streamlain.settings.workspace.permissions.who-has-access'
          ]?.() ?? 'Who has access'}
        </span>
        {presentation !== 'access' ? (
          <Button
            data-testid={`${config.testIdPrefix}-add-button`}
            variant="plain"
            prefix={<PlusIcon />}
            onClick={() => setShowInvite(!showInvite)}
            disabled={isUpdating}
          >
            {t['com.streamlain.settings.workspace.permissions.add-user']?.() ??
              'Add'}
          </Button>
        ) : null}
      </div>

      {/* Invite Section */}
      {presentation !== 'access' && showInvite && (
        <>
          {/* User/Group Toggle */}
          <div className={styles.inviteContainer}>
            {config.allowGroups !== false ? (
              <div style={{ display: 'flex', gap: 4, marginRight: 8 }}>
                <button
                  data-testid={`${config.testIdPrefix}-invite-users`}
                  className={styles.modeButton}
                  data-active={inviteMode === 'users'}
                  onClick={() => {
                    setInviteMode('users');
                    setSearchQuery('');
                  }}
                >
                  <UserIcon style={{ width: 14, height: 14 }} />
                  {t[
                    'com.streamlain.settings.workspace.permissions.users'
                  ]?.() ?? 'Users'}
                </button>
                <button
                  data-testid={`${config.testIdPrefix}-invite-groups`}
                  className={styles.modeButton}
                  data-active={inviteMode === 'groups'}
                  onClick={() => {
                    setInviteMode('groups');
                    setSearchQuery('');
                  }}
                >
                  <GroupIcon style={{ width: 14, height: 14 }} />
                  {t[
                    'com.streamlain.settings.workspace.permissions.groups'
                  ]?.() ?? 'Groups'}
                </button>
              </div>
            ) : null}
            {inviteMode === 'users' && (
              <div className={styles.inviteSearch}>
                <SearchIcon className={styles.searchIcon} />
                <RowInput
                  data-testid={`${config.testIdPrefix}-search-input`}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={
                    t[
                      'com.streamlain.settings.workspace.permissions.search-members'
                    ]?.() ?? 'Search members...'
                  }
                  className={styles.searchInputField}
                />
              </div>
            )}
            {presentation === 'full' ? (
              <Menu
                items={config.grantableRoles.map(role => (
                  <MenuItem
                    key={role}
                    data-testid={`${config.testIdPrefix}-grant-role-${role}`}
                    selected={role === selectedRole}
                    onSelect={() => setSelectedRole(role)}
                  >
                    {getRoleLabel(role)}
                  </MenuItem>
                ))}
                contentOptions={{ align: 'end' }}
              >
                <MenuTrigger
                  data-testid={`${config.testIdPrefix}-role-selector`}
                  className={styles.roleMenuTrigger}
                >
                  {getRoleLabel(selectedRole)}
                </MenuTrigger>
              </Menu>
            ) : null}
          </div>

          {/* Available members list */}
          {inviteMode === 'users' && availableMembers.length > 0 && (
            <Scrollable.Root className={styles.inviteMembersList}>
              <Scrollable.Viewport>
                {availableMembers.map(member => (
                  <div
                    key={member.id}
                    data-testid={`${config.testIdPrefix}-invite-member-${member.id}`}
                    className={styles.inviteMemberRow}
                    onClick={() => {
                      void handleGrantUser(member.id);
                    }}
                  >
                    <SettingsEntityAvatar
                      size={28}
                      userId={member.id}
                      name={member.name || member.email}
                      url={member.avatarUrl}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>
                        {member.name || member.email}
                      </div>
                      {member.name && (
                        <div className={styles.listItemSubtitle}>
                          {member.email}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </Scrollable.Viewport>
              <Scrollable.Scrollbar />
            </Scrollable.Root>
          )}

          {inviteMode === 'users' &&
            availableMembers.length === 0 &&
            searchQuery && (
              <div className={styles.noMembersFound}>
                {t[
                  'com.streamlain.settings.workspace.permissions.no-members-found'
                ]?.() ?? 'No members found'}
              </div>
            )}

          {/* Available groups list */}
          {config.allowGroups !== false &&
            inviteMode === 'groups' &&
            availableGroups.length > 0 && (
              <Scrollable.Root className={styles.inviteMembersList}>
                <Scrollable.Viewport>
                  {availableGroups.map(group => (
                    <div
                      key={group.id}
                      data-testid={`${config.testIdPrefix}-invite-group-${group.id}`}
                      className={styles.inviteMemberRow}
                      onClick={() => {
                        void handleGrantGroup(group.id);
                      }}
                    >
                      <GroupIcon
                        style={{ width: 28, height: 28, flexShrink: 0 }}
                      />
                      <div className={styles.listItemContent}>
                        <div className={styles.listItemTitle}>{group.name}</div>
                        {group.description && (
                          <div className={styles.listItemSubtitle}>
                            {group.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </Scrollable.Viewport>
                <Scrollable.Scrollbar />
              </Scrollable.Root>
            )}

          {config.allowGroups !== false &&
            inviteMode === 'groups' &&
            availableGroups.length === 0 && (
              <div className={styles.noMembersFound}>
                {t[
                  'com.streamlain.settings.workspace.permissions.no-groups-found'
                ]?.() ?? 'No groups available'}
              </div>
            )}
        </>
      )}

      {/* Granted entries list */}
      {entries.length === 0 ? (
        <div className={styles.emptyPermissions}>
          {emptyMessage ??
            t[
              'com.streamlain.settings.workspace.permissions.no-granted-users'
            ]?.() ??
            'No specific permissions set. All workspace members have default access.'}
        </div>
      ) : (
        <div className={styles.grantedUsersList}>
          {/* User entries */}
          {userEntries.map(entry => {
            const isOwner =
              config.ownerRole !== undefined && entry.role === config.ownerRole;
            const isRoleImmutable = config.immutableRoles?.includes(entry.role);
            const displayName = entry.name || entry.email || 'User';
            const areaBlockedNotice = getAreaBlockedNotice(t, entry);

            return (
              <div
                key={`user-${entry.targetId}`}
                data-testid={`${config.testIdPrefix}-row-user-${entry.targetId}`}
                className={styles.permissionRowWrapper}
                data-area-blocked={entry.isAreaBlocked ? 'true' : undefined}
              >
                <div
                  className={`${styles.permissionRow} ${
                    entry.isAreaBlocked ? styles.areaBlockedPermissionRow : ''
                  }`}
                >
                  <div className={styles.permissionUser}>
                    <SettingsEntityAvatar
                      size={28}
                      userId={entry.targetId}
                      name={displayName}
                      url={entry.avatarUrl}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{displayName}</div>
                      <div className={styles.listItemSubtitle}>
                        {entry.email}
                      </div>
                    </div>
                  </div>

                  {presentation === 'people' ? null : isRoleImmutable ? (
                    <span className={styles.ownerMenuTrigger}>
                      {getRoleLabel(entry.role)}
                    </span>
                  ) : (
                    <Menu
                      items={config.allRoles
                        .filter(
                          role =>
                            role !== config.ownerRole ||
                            Boolean(config.demoteOnTransferRole)
                        )
                        .map(role => (
                          <MenuItem
                            key={role}
                            selected={role === entry.role}
                            onSelect={() => {
                              void handleUpdateRole(entry, role);
                            }}
                          >
                            {getRoleLabel(role)}
                          </MenuItem>
                        ))}
                      contentOptions={{ align: 'end' }}
                    >
                      <MenuTrigger
                        data-testid={`${config.testIdPrefix}-role-trigger-user-${entry.targetId}`}
                        className={
                          isOwner
                            ? styles.ownerMenuTrigger
                            : styles.roleMenuTrigger
                        }
                        disabled={isUpdating}
                      >
                        {getRoleLabel(entry.role)}
                      </MenuTrigger>
                    </Menu>
                  )}

                  {!isOwner && (
                    <Button
                      data-testid={`${config.testIdPrefix}-remove-user-${entry.targetId}`}
                      variant="plain"
                      className={styles.removeButton}
                      onClick={() => {
                        handleRevoke(entry);
                      }}
                      disabled={isUpdating}
                    >
                      <DeleteIcon />
                    </Button>
                  )}
                </div>
                {areaBlockedNotice ? (
                  <div className={styles.areaBlockedNotice}>
                    {areaBlockedNotice}
                  </div>
                ) : null}
              </div>
            );
          })}

          {/* Group entries */}
          {groupEntries.map(entry => {
            const areaBlockedNotice = getAreaBlockedNotice(t, entry);

            return (
              <div
                key={`group-${entry.targetId}`}
                data-testid={`${config.testIdPrefix}-row-group-${entry.targetId}`}
                className={styles.permissionRowWrapper}
                data-area-blocked={entry.isAreaBlocked ? 'true' : undefined}
              >
                <div
                  className={`${styles.permissionRow} ${
                    entry.isAreaBlocked ? styles.areaBlockedPermissionRow : ''
                  }`}
                >
                  <div className={styles.permissionUser}>
                    <GroupIcon
                      style={{ width: 28, height: 28, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{entry.name}</div>
                      <div className={styles.listItemSubtitle}>
                        {t[
                          'com.streamlain.settings.workspace.permissions.group'
                        ]?.() ?? 'Group'}
                      </div>
                    </div>
                  </div>

                  {presentation === 'people' ? null : (
                    <Menu
                      items={config.grantableRoles.map(role => (
                        <MenuItem
                          key={role}
                          selected={role === entry.role}
                          onSelect={() => {
                            void handleUpdateRole(entry, role);
                          }}
                        >
                          {getRoleLabel(role)}
                        </MenuItem>
                      ))}
                      contentOptions={{ align: 'end' }}
                    >
                      <MenuTrigger
                        data-testid={`${config.testIdPrefix}-role-trigger-group-${entry.targetId}`}
                        className={styles.roleMenuTrigger}
                        disabled={isUpdating}
                      >
                        {getRoleLabel(entry.role)}
                      </MenuTrigger>
                    </Menu>
                  )}

                  <Button
                    data-testid={`${config.testIdPrefix}-remove-group-${entry.targetId}`}
                    variant="plain"
                    className={styles.removeButton}
                    onClick={() => {
                      handleRevoke(entry);
                    }}
                    disabled={isUpdating}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
                {areaBlockedNotice ? (
                  <div className={styles.areaBlockedNotice}>
                    {areaBlockedNotice}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
